import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
  approveLocation,
  approveRoute,
  blockUserFromDashboardMessageStream,
  deleteDashboardMessageStreamItem,
  getDashboardModeration,
  listDashboardMessageStream,
  listLocationModerationQueue,
  listRouteModerationQueue,
  rejectLocation,
  rejectRoute,
  type DashboardMessageStreamItem,
  type LocationItem,
  type RouteItem,
} from '../lib/api';
import { renderLocationSidebar } from '../templates/locationSidebar';

type QueueModule = 'locations' | 'routes';
type MessageModule = 'location_review' | 'route_review' | 'news_review' | 'event_review';

type ModerationQueueRow = {
  key: string;
  module: QueueModule;
  id: number;
  title: string;
  subtitle: string;
  submitter: string;
  createdAt: string;
  status: string;
  viewKey: string;
};

const statusLabel = (status: string): string =>
  status.split('_').join(' ').replace(/\b\w/g, (s: string) => s.toUpperCase());

const formatDate = (value?: string): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const queueModuleLabel = (module: QueueModule): string => (module === 'locations' ? 'Locations' : 'Routes');

const messageModuleLabel = (module: MessageModule): string => {
  if (module === 'location_review') return 'Location Review';
  if (module === 'route_review') return 'Route Review';
  if (module === 'news_review') return 'News Review';
  return 'Event Review';
};

const messageModuleBadgeClass = (module: MessageModule): string => {
  if (module === 'location_review') return 'bg-blue-50 text-blue-600';
  if (module === 'route_review') return 'bg-indigo-50 text-indigo-600';
  if (module === 'news_review') return 'bg-emerald-50 text-emerald-600';
  return 'bg-amber-50 text-amber-700';
};

const moduleLink = (module: MessageModule, parentId: number): string => {
  if (module === 'location_review') return `/locations?view=${parentId}`;
  if (module === 'route_review') return `/routes?view=${parentId}`;
  if (module === 'news_review') return `/news?view=${parentId}`;
  return `/events?view=${parentId}`;
};

const toLocationQueueRow = (item: LocationItem): ModerationQueueRow => ({
  key: `locations-${item.id}`,
  module: 'locations',
  id: item.id,
  title: item.title,
  subtitle: item.location_name || `${item.latitude}, ${item.longitude}`,
  submitter: item.submitter_display || item.submitter_username || '-',
  createdAt: item.created_at,
  status: item.status,
  viewKey: `locations-${item.id}`,
});

const toRouteQueueRow = (item: RouteItem): ModerationQueueRow => ({
  key: `routes-${item.id}`,
  module: 'routes',
  id: item.id,
  title: item.title,
  subtitle: item.location_name || `${item.distance_km} km`,
  submitter: item.source || '-',
  createdAt: item.created_at,
  status: item.status,
  viewKey: `routes-${item.id}`,
});

export default function ModerationPage() {
  const sidebar = useMemo(() => renderLocationSidebar('moderation'), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyRowKey, setBusyRowKey] = useState<string | null>(null);
  const [busyMessageKey, setBusyMessageKey] = useState<string | null>(null);
  const [queueRows, setQueueRows] = useState<ModerationQueueRow[]>([]);
  const [messageRows, setMessageRows] = useState<DashboardMessageStreamItem[]>([]);
  const [queueQuery, setQueueQuery] = useState('');
  const [messageQuery, setMessageQuery] = useState('');
  const [queueModuleFilter, setQueueModuleFilter] = useState<'all' | QueueModule>('all');
  const [messageModuleFilter, setMessageModuleFilter] = useState<'all' | MessageModule>('all');
  const [kpi, setKpi] = useState({
    totalQueue: 0,
    pendingLocations: 0,
    pendingRoutes: 0,
    messagesTotal: 0,
  });

  const loadData = async () => {
    const [locations, routes, moderationSummary, messages] = await Promise.all([
      listLocationModerationQueue({ page: 1, page_size: 100 }),
      listRouteModerationQueue({ page: 1, page_size: 100 }),
      getDashboardModeration(),
      listDashboardMessageStream({ page: 1, page_size: 200 }),
    ]);

    const nextQueueRows = [
      ...locations.results.map(toLocationQueueRow),
      ...routes.results.map(toRouteQueueRow),
    ].sort((a, b) => (new Date(b.createdAt).getTime() || 0) - (new Date(a.createdAt).getTime() || 0));

    setQueueRows(nextQueueRows);
    setMessageRows(messages.results);
    setKpi({
      totalQueue: moderationSummary.total_queue,
      pendingLocations: moderationSummary.pending_locations,
      pendingRoutes: moderationSummary.pending_routes,
      messagesTotal: moderationSummary.messages_total,
    });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        await loadData();
      } catch {
        if (!cancelled) setError('Failed to load moderation data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleQuickAction = async (row: ModerationQueueRow, action: 'approve' | 'reject') => {
    try {
      setBusyRowKey(row.key);
      if (row.module === 'locations') {
        if (action === 'approve') await approveLocation(row.id, 'Quick moderation');
        else await rejectLocation(row.id, 'Quick moderation');
      } else {
        if (action === 'approve') await approveRoute(row.id, 'Quick moderation');
        else await rejectRoute(row.id, 'Quick moderation');
      }
      await loadData();
    } catch {
      setError(`Failed to ${action} ${row.key}.`);
    } finally {
      setBusyRowKey(null);
    }
  };

  const filteredQueueRows = queueRows.filter((row) => {
    if (queueModuleFilter !== 'all' && row.module !== queueModuleFilter) return false;
    const q = queueQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      row.key.toLowerCase().includes(q)
      || row.title.toLowerCase().includes(q)
      || row.subtitle.toLowerCase().includes(q)
      || row.submitter.toLowerCase().includes(q)
    );
  });

  const filteredMessageRows = messageRows.filter((row) => {
    if (messageModuleFilter !== 'all' && row.module !== messageModuleFilter) return false;
    const q = messageQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      row.text.toLowerCase().includes(q)
      || row.parent_title.toLowerCase().includes(q)
      || row.author_name.toLowerCase().includes(q)
    );
  });

  const handleDeleteMessage = async (item: DashboardMessageStreamItem) => {
    const messageKey = `${item.module}-${item.id}`;
    try {
      setBusyMessageKey(messageKey);
      await deleteDashboardMessageStreamItem(item.module, item.id);
      await loadData();
    } catch {
      setError('Failed to delete message.');
    } finally {
      setBusyMessageKey(null);
    }
  };

  const handleBlockUser = async (item: DashboardMessageStreamItem) => {
    if (!item.author_id) return;
    const messageKey = `${item.module}-${item.id}`;
    try {
      setBusyMessageKey(messageKey);
      await blockUserFromDashboardMessageStream(item.author_id);
      await loadData();
    } catch {
      setError('Failed to block user.');
    } finally {
      setBusyMessageKey(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>MotoJourney | Moderation</title>
      </Helmet>
      <div className="flex min-h-screen bg-background-light">
        <div dangerouslySetInnerHTML={{ __html: sidebar }} />
        <main className="ml-64 flex-1 overflow-y-auto p-8">
          <header className="mb-6">
            <h2 className="text-3xl font-black tracking-tight">Moderation</h2>
            <p className="mt-1 text-slate-500">Locations/Routes queue and unified message stream</p>
          </header>

          <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Total Queue</p>
              <p className="mt-2 text-3xl font-bold text-slate-900">{kpi.totalQueue}</p>
            </article>
            <article className="rounded-xl border border-blue-100 bg-blue-50/50 p-5 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wider text-blue-500">Pending Locations</p>
              <p className="mt-2 text-3xl font-bold text-blue-700">{kpi.pendingLocations}</p>
            </article>
            <article className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-5 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wider text-indigo-500">Pending Routes</p>
              <p className="mt-2 text-3xl font-bold text-indigo-700">{kpi.pendingRoutes}</p>
            </article>
            <article className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-5 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wider text-emerald-600">Messages Stream</p>
              <p className="mt-2 text-3xl font-bold text-emerald-700">{kpi.messagesTotal}</p>
            </article>
          </section>

          <section className="mb-8 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <header className="border-b border-slate-200 px-6 py-4">
              <h3 className="text-lg font-bold text-slate-900">Moderation Queue (Locations & Routes)</h3>
            </header>

            <div className="grid grid-cols-1 gap-3 border-b border-slate-200 p-4 lg:grid-cols-4">
              <input
                value={queueQuery}
                onChange={(event) => setQueueQuery(event.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Search queue..."
              />
              <select
                value={queueModuleFilter}
                onChange={(event) => setQueueModuleFilter(event.target.value as 'all' | QueueModule)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="all">All Modules</option>
                <option value="locations">Locations</option>
                <option value="routes">Routes</option>
              </select>
              <button
                onClick={() => {
                  setQueueQuery('');
                  setQueueModuleFilter('all');
                }}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Reset
              </button>
            </div>

            {loading ? (
              <div className="p-6 text-sm text-slate-500">Loading moderation queue...</div>
            ) : error ? (
              <div className="p-6 text-sm text-red-600">{error}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/80">
                      <th className="p-4 text-[11px] font-bold uppercase text-slate-400">ID</th>
                      <th className="p-4 text-[11px] font-bold uppercase text-slate-400">Module</th>
                      <th className="p-4 text-[11px] font-bold uppercase text-slate-400">Title / Preview</th>
                      <th className="p-4 text-[11px] font-bold uppercase text-slate-400">Submitter</th>
                      <th className="p-4 text-[11px] font-bold uppercase text-slate-400">Submitted</th>
                      <th className="p-4 text-center text-[11px] font-bold uppercase text-slate-400">Status</th>
                      <th className="p-4 text-right text-[11px] font-bold uppercase text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredQueueRows.map((item) => (
                      <tr key={item.key} className="transition-colors hover:bg-slate-50/60">
                        <td className="p-4 font-mono text-xs text-slate-500">{item.key}</td>
                        <td className="p-4">
                          <span className="rounded bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase text-slate-700">
                            {queueModuleLabel(item.module)}
                          </span>
                        </td>
                        <td className="min-w-[240px] p-4">
                          <a href={`/moderation/${item.viewKey}`} className="block truncate text-sm font-semibold hover:text-primary">
                            {item.title}
                          </a>
                          <p className="truncate text-[11px] text-slate-400">{item.subtitle || '-'}</p>
                        </td>
                        <td className="p-4 text-xs font-medium">{item.submitter || '-'}</td>
                        <td className="p-4 text-xs text-slate-500">{formatDate(item.createdAt)}</td>
                        <td className="p-4 text-center">
                          <span className="rounded bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase text-slate-700">
                            {statusLabel(item.status)}
                          </span>
                        </td>
                        <td className="space-x-1 p-4 text-right">
                          <button
                            disabled={busyRowKey === item.key}
                            onClick={() => handleQuickAction(item, 'approve')}
                            className="p-1 text-slate-400 transition-colors hover:text-emerald-500 disabled:opacity-40"
                            title="Quick Approve"
                          >
                            <span className="material-symbols-outlined text-lg">check_circle</span>
                          </button>
                          <button
                            disabled={busyRowKey === item.key}
                            onClick={() => handleQuickAction(item, 'reject')}
                            className="p-1 text-slate-400 transition-colors hover:text-red-500 disabled:opacity-40"
                            title="Quick Reject"
                          >
                            <span className="material-symbols-outlined text-lg">cancel</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <header className="border-b border-slate-200 px-6 py-4">
              <h3 className="text-lg font-bold text-slate-900">Message Stream (No Moderation)</h3>
            </header>

            <div className="grid grid-cols-1 gap-3 border-b border-slate-200 p-4 lg:grid-cols-4">
              <input
                value={messageQuery}
                onChange={(event) => setMessageQuery(event.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Search messages..."
              />
              <select
                value={messageModuleFilter}
                onChange={(event) => setMessageModuleFilter(event.target.value as 'all' | MessageModule)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="all">All Modules</option>
                <option value="location_review">Location Review</option>
                <option value="route_review">Route Review</option>
                <option value="news_review">News Review</option>
                <option value="event_review">Event Review</option>
              </select>
              <button
                onClick={() => {
                  setMessageQuery('');
                  setMessageModuleFilter('all');
                }}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Reset
              </button>
            </div>

            {loading ? (
              <div className="p-6 text-sm text-slate-500">Loading messages...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/80">
                      <th className="p-4 text-[11px] font-bold uppercase text-slate-400">Module</th>
                      <th className="p-4 text-[11px] font-bold uppercase text-slate-400">Parent</th>
                      <th className="p-4 text-[11px] font-bold uppercase text-slate-400">Message</th>
                      <th className="p-4 text-[11px] font-bold uppercase text-slate-400">Author</th>
                      <th className="p-4 text-[11px] font-bold uppercase text-slate-400">Date</th>
                      <th className="p-4 text-right text-[11px] font-bold uppercase text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredMessageRows.map((item) => (
                      <tr key={`${item.module}-${item.id}`} className="transition-colors hover:bg-slate-50/60">
                        <td className="p-4">
                          <span className={`rounded px-2 py-1 text-[10px] font-bold uppercase ${messageModuleBadgeClass(item.module)}`}>
                            {messageModuleLabel(item.module)}
                          </span>
                        </td>
                        <td className="max-w-[220px] p-4">
                          <p className="truncate text-sm font-semibold text-slate-900">{item.parent_title}</p>
                          <p className="text-[11px] text-slate-400">#{item.parent_id}</p>
                        </td>
                        <td className="max-w-[420px] p-4">
                          <p className="line-clamp-2 text-sm text-slate-700">{item.text}</p>
                        </td>
                        <td className={`p-4 text-sm ${item.author_is_blocked ? 'font-semibold text-red-600' : 'text-slate-700'}`}>
                          {item.author_name || '-'}
                        </td>
                        <td className="p-4 text-xs text-slate-500">{formatDate(item.created_at)}</td>
                        <td className="space-x-1 p-4 text-right">
                          <button
                            disabled={busyMessageKey === `${item.module}-${item.id}`}
                            onClick={() => handleDeleteMessage(item)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40"
                            title="Delete message"
                            aria-label="Delete message"
                          >
                            <span className="material-symbols-outlined text-base">delete</span>
                          </button>
                          <button
                            disabled={busyMessageKey === `${item.module}-${item.id}` || !item.author_id || item.author_is_blocked}
                            onClick={() => handleBlockUser(item)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-amber-200 text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-40"
                            title="Block user"
                            aria-label="Block user"
                          >
                            <span className="material-symbols-outlined text-base">person_off</span>
                          </button>
                          <a
                            href={moduleLink(item.module, item.parent_id)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-700 transition-colors hover:bg-slate-50"
                            title="Open related record"
                            aria-label="Open related record"
                          >
                            <span className="material-symbols-outlined text-base">open_in_new</span>
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    </>
  );
}
